import { Injectable, inject, signal } from '@angular/core';
import { NavigationStart, Router } from '@angular/router';
import { AGENT_CATALOG } from './agent-catalog';

/** One thing to point at: a catalog anchor or a marked object, with a short text. */
export interface SpotlightStep {
  anchor?: string;
  target?: string;
  title?: string;
  body?: string;
  /** A second, smaller line: the tricky part of a step or another way to do it. */
  tip?: string;
  /** Catalog page the step lives on. A tour whose steps name pages survives
   *  navigation between those pages (Academy click paths across pages). */
  page?: string;
  /** The element appears only after the person acts (opens a tab, a dialog,
   *  another page): wait for it instead of dropping the step after 3 s. */
  await?: boolean;
  /** Clicking the element moves the tour on — the person does the step
   *  themselves instead of pressing "Next". */
  advance?: 'click';
  /** Fallback anchors, tried in order when `anchor` is not on screen (e.g.
   *  no Alpha Tutor tile → the first Alpha Flexible tile). */
  alt?: string[];
  /** Skip this step when that anchor is already visible (e.g. the „Dozent“
   *  group is open, its links show — no need to click the group). */
  skipIfVisible?: string;
}

export interface SpotlightState {
  kind: 'highlight' | 'tour';
  steps: SpotlightStep[];
  index: number;
  /** Where the last step leads instead of just closing — the Academy sends
   *  the person back to the lesson's quiz (Marcel, 29.10.2026). */
  finish?: { label: string; url: string };
}

/** The path of a URL without query and fragment: a filter or tab-in-query change is the same page. */
function pathOf(url: string): string {
  return url.split(/[?#]/)[0];
}

/** Does `path` belong to catalog page `page`? Route parameters match any
 *  segment; a missing LAST parameter counts too — menus link to
 *  `/tutors/<id>/knowledge`, which redirects to `…/knowledge/base`, and the
 *  tour must survive that first hop. */
export function onPage(page: string | undefined, path: string): boolean {
  const route = page ? AGENT_CATALOG.pages[page]?.route : undefined;
  if (!route) return false;
  const pattern = route
    .replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
    .replace(/\/:[^/]+$/, '(?:/[^/]+)?')
    .replace(/:[^/]+/g, '[^/]+');
  return new RegExp(`^${pattern}/?$`).test(path);
}

/**
 * What the spotlight shows right now. The agent's `highlight` and `tour`
 * actions land here (AgentUiService); `app-agent-spotlight` draws it.
 * A highlight is one ring with a note that goes away by itself; a tour is a
 * sequence the user steps through.
 *
 * Both belong to the page they were made for: any navigation to another path
 * ends them — by the agent or by the user (bug #188: the card for
 * „Lernprozess" stayed on Wissensbasis and ringed its file table). A highlight
 * the agent means for the target page is set after its navigation
 * (AgentUiService), so it survives.
 */
@Injectable({ providedIn: 'root' })
export class AgentSpotlightService {
  private readonly stateSignal = signal<SpotlightState | null>(null);
  readonly state = this.stateSignal.asReadonly();
  private readonly router = inject(Router);

  constructor() {
    // Root service: lives as long as the app, so no teardown.
    this.router.events.subscribe((event) => {
      const state = this.stateSignal();
      if (!(event instanceof NavigationStart) || !state || pathOf(event.url) === pathOf(this.router.url)) return;
      // A multi-page tour stays while the person moves between its own pages.
      if (state.kind === 'tour' && state.steps.some((s) => onPage(s.page, pathOf(event.url)))) return;
      this.close();
    });
  }

  highlight(step: SpotlightStep): void {
    this.stateSignal.set({ kind: 'highlight', steps: [step], index: 0 });
  }

  tour(steps: SpotlightStep[], finish?: SpotlightState['finish']): void {
    if (steps.length) this.stateSignal.set({ kind: 'tour', steps, index: 0, finish });
  }

  /** Last step's own action: close and go where the tour came from. */
  finishTour(): void {
    const url = this.stateSignal()?.finish?.url;
    this.close();
    if (url) this.router.navigateByUrl(url);
  }

  next(): void {
    const s = this.stateSignal();
    if (!s) return;
    if (s.index + 1 >= s.steps.length) this.close();
    else this.stateSignal.set({ ...s, index: s.index + 1 });
  }

  back(): void {
    const s = this.stateSignal();
    if (s && s.index > 0) this.stateSignal.set({ ...s, index: s.index - 1 });
  }

  /** Drops the current step (its element never appeared) and moves on. */
  skip(): void {
    const s = this.stateSignal();
    if (!s) return;
    const steps = s.steps.filter((_, i) => i !== s.index);
    if (!steps.length) this.close();
    else this.stateSignal.set({ ...s, steps, index: Math.min(s.index, steps.length - 1) });
  }

  close(): void {
    this.stateSignal.set(null);
  }
}
